import { useEffect, useState } from 'react';
import { Alert, Box, Card, CardContent } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAnalytics } from '../../../../api/analytics';
import CustomCircularProgress from '../../../../components/ui/customCircularProgress/customCircularProgress.component';
import { useHasPermission } from '../../../../contexts/auth';
import { EquipmentFullServiceStats } from '../../../../types/analytics';
import EquipmentAutocomplete from '../../../../components/ui/equipmentAutocomplete/equipmentAutocomplete.component';
import DateRangeFilter from '../../../../components/ui/dateRangeFilter/dateRangeFilter.component';
import EquipmentServiceStatsContent from '../../../../components/service/equipmentServiceStatsContent/equipmentServiceStatsContent.component';
import { useDateRangeQueryParams } from '../../../../hooks/useDateRangeQueryParams';

interface Equipment {
  id: string;
  name?: string;
  full_name?: string;
  [key: string]: any;
}

export const EquipmentStats = () => {
  const { t } = useTranslation();
  const canViewJobs = useHasPermission('service.view_job');
  const { loading, error, getEquipmentFullServiceStats } = useAnalytics();
  const [equipment, setEquipment] = useState<Equipment | null>(null);
  const { dateRange, setDateRange } = useDateRangeQueryParams();
  const [stats, setStats] = useState<EquipmentFullServiceStats | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      if (!equipment || !canViewJobs) {
        setStats(null);
        return;
      }
      const data = await getEquipmentFullServiceStats(equipment.id, dateRange);
      if (!cancelled) {
        setStats(data || null);
      }
    };

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [equipment, dateRange, canViewJobs, getEquipmentFullServiceStats]);

  if (!canViewJobs) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
      <Card>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Wrapped in its own Box: EquipmentAutocomplete renders a Fragment
              (autocomplete + an "add equipment" modal wrapper), and without
              this wrapper the modal's invisible node becomes its own flex
              item here, inserting an extra gap before the date pickers. */}
          <Box>
            <EquipmentAutocomplete
              value={equipment}
              onChange={setEquipment}
              filterUnattached={false}
            />
          </Box>
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
        </CardContent>
      </Card>

      {!equipment && (
        <Alert severity="info">{t('Select an equipment to see its statistics')}</Alert>
      )}

      {equipment && loading && !stats && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CustomCircularProgress />
        </Box>
      )}

      {equipment && error && (
        <Alert severity="error">{t('There was an error, please review the form.')}</Alert>
      )}

      {equipment && stats && (
        <EquipmentServiceStatsContent
          equipmentId={equipment.id}
          stats={stats}
          dateRange={dateRange}
        />
      )}
    </Box>
  );
};
